import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { FlatpickrDatePickerComponent } from '../../../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { LookupWorkshop } from '../../../../../_shared/models/lookup.model';
import { ServiceTypes } from '../../../../../_shared/enums/service-type.enum';
import { EditEventScheduleRequest } from '../../../models/maintenance-calendar-request.model';
import { EventScheduleResponse } from '../../../models/maintenance-calendar-response.model';
import { MaintenanceOverviewService } from '../../../services/maintenance-overview.service';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-event-schedule-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgbModule, FlatpickrDatePickerComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './event-schedule-modal.component.html',
  styleUrl: './event-schedule-modal.component.scss'
})
export class EventScheduleModalComponent implements OnDestroy {
  private _eventData?: EventScheduleResponse;

  @Input() workOrderId!: number;
  @Input()
  set eventData(value: EventScheduleResponse | undefined) {
    this._eventData = value;
    if (value) {
      this.getServiceCentresAsync();
      this.populateForm();
    }
  }

  get eventData(): EventScheduleResponse | undefined {
    return this._eventData;
  }

  form: FormGroup;
  isLoading = false;
  isLoadingServiceCentres = false;
  isEditingDetails = false;
  hasChanges = false;
  scheduledDateValue = '';

  serviceCentres: LookupWorkshop[] = [];

  private transloco = inject(TranslocoService);

  serviceTypeLabels: Record<ServiceTypes, string> = {
    [ServiceTypes.Minor]: this.transloco.translate('maintenanceOverview.serviceType.minor'),
    [ServiceTypes.Intermediate]: this.transloco.translate('maintenanceOverview.serviceType.intermediate'),
    [ServiceTypes.Major]: this.transloco.translate('maintenanceOverview.serviceType.major')
  };
  serviceTypeOptions = Object.entries(this.serviceTypeLabels).map(([value, label]) => ({
    value: +value as ServiceTypes,
    label
  }));

  selectedServiceType = '';
  selectedServiceCentre = '';
  private initialFormValues: {
    serviceType: ServiceTypes | null;
    serviceCentre: number | null;
    scheduledDate: string | null;
  } | null = null;

  private serviceCentresCancelSubject: Subject<void> = new Subject<void>();
  private updateEventScheduleCancelSubject: Subject<void> = new Subject<void>();

  private maintenanceService = inject(MaintenanceOverviewService);
  private errorHandlerService = inject(ErrorHandlerService);
  private toastr = inject(ToastrService);
  private activeModal = inject(NgbActiveModal);
  private fb = inject(FormBuilder);

  constructor() {
    this.form = this.fb.group({
      asset: [{ value: '', disabled: true }],
      make: [{ value: '', disabled: true }],
      model: [{ value: '', disabled: true }],
      odometer: [{ value: '', disabled: true }],
      serviceType: [null as ServiceTypes | null, Validators.required],
      serviceCentre: [null as number | null, Validators.required],
      scheduledDate: [null as string | null, Validators.required],
      serviceItems: [[] as string[]]
    });
  }

  ngOnDestroy(): void {
    this.serviceCentresCancelSubject.next();
    this.serviceCentresCancelSubject.complete();
    this.updateEventScheduleCancelSubject.next();
    this.updateEventScheduleCancelSubject.complete();
  }

  get assetLabel(): string {
    return this.form.get('asset')?.value || this.transloco.translate('maintenanceOverview.modals.eventSchedule.noAsset');
  }

  get serviceTypeLabel(): string {
    return this.selectedServiceType || this.transloco.translate('maintenanceOverview.modals.eventSchedule.selectMaintenanceType');
  }

  get serviceCentreLabel(): string {
    if (this.isEditingDetails && this.form.get('serviceCentre')?.value == null) {
      return this.transloco.translate('maintenanceOverview.modals.eventSchedule.selectServiceCentre');
    }
    return this.selectedServiceCentre || this.transloco.translate('maintenanceOverview.modals.eventSchedule.selectServiceCentre');
  }

  get selectedItems(): string[] {
    return (this.form.get('serviceItems')?.value as string[]) ?? [];
  }

  async getServiceCentresAsync(): Promise<void> {
    this.isLoadingServiceCentres = true;

    try {
      this.serviceCentresCancelSubject.next();
      const response = await this.maintenanceService.getServiceCentres(this.serviceCentresCancelSubject);
      this.serviceCentres = response.data ?? [];

      if (this.eventData) {
        const workshopId = this.resolveWorkshopIdByName(this.eventData.serviceCentre);
        this.form.get('serviceCentre')?.setValue(workshopId);

        const resolvedName = this.serviceCentres.find(w => w.id === workshopId)?.name;
        this.selectedServiceCentre = resolvedName || this.eventData.serviceCentre || '';

        if (this.initialFormValues) {
          this.initialFormValues.serviceCentre = workshopId;
          this.checkForChanges();
        } else {
          this.storeInitialValues();
        }
      }

      this.isLoadingServiceCentres = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isLoadingServiceCentres = false;
        }
      });
    }
  }

  onServiceTypeSelect(type: ServiceTypes, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.selectedServiceType = this.serviceTypeLabels[type] || '';
    this.form.get('serviceType')?.setValue(type);
    this.form.get('serviceType')?.markAsTouched();
    this.checkForChanges();
  }

  onServiceCentreSelect(workshop: LookupWorkshop, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.selectedServiceCentre = workshop.name;
    this.form.get('serviceCentre')?.setValue(workshop.id);
    this.form.get('serviceCentre')?.markAsTouched();
    this.checkForChanges();
  }

  onScheduledDateChange(value: string | undefined): void {
    const next = this.normalizeScheduledDateValue(value);
    const current = this.normalizeScheduledDateValue(this.scheduledDateValue);

    if (next === current) {
      return;
    }

    this.scheduledDateValue = next ?? '';
    this.form.get('scheduledDate')?.setValue(this.scheduledDateValue);
    this.form.get('scheduledDate')?.markAsTouched();
    this.checkForChanges();
  }

  toggleEditDetails(): void {
    this.isEditingDetails = !this.isEditingDetails;
  }

  closeModal(): void {
    this.activeModal.close();
  }

  onCancel(): void {
    this.activeModal.dismiss();
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid || !this.scheduledDateValue || !this.workOrderId) {
      Object.keys(this.form.controls).forEach(key => this.form.get(key)?.markAsTouched());
      return;
    }

    const scheduled = new Date(this.scheduledDateValue.replace(/\//g, '-'));
    if (Number.isNaN(scheduled.getTime())) return;

    this.isLoading = true;

    try {
      this.updateEventScheduleCancelSubject.next();

      const raw = this.form.getRawValue();
      const request: EditEventScheduleRequest = {
        workOrderId: this.workOrderId,
        assetId: this.eventData?.assetId ?? 0,
        serviceType: raw.serviceType,
        workshopId: raw.serviceCentre,
        scheduledDate: scheduled
      };

      await this.maintenanceService.updateEventSchedule(request, this.updateEventScheduleCancelSubject);
      this.toastr.success(this.transloco.translate('maintenanceOverview.toasts.eventScheduleUpdated'));
      this.isLoading = false;

      this.activeModal.close(true);
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  private populateForm(): void {
    if (!this.eventData) return;

    this.scheduledDateValue = this.formatScheduledForInput(new Date(this.eventData.scheduledDate));
    this.selectedServiceType = this.serviceTypeLabels[this.eventData.serviceType] || '';

    const workshopId = this.resolveWorkshopIdByName(this.eventData.serviceCentre);
    const resolvedName = this.serviceCentres.find(w => w.id === workshopId)?.name;
    this.selectedServiceCentre = resolvedName || this.eventData.serviceCentre || '';

    this.form.patchValue({
      asset: this.eventData.assetName || '',
      make: this.eventData.make || '',
      model: this.eventData.model || '',
      odometer: this.formatOdometerReading(this.eventData.odometerReading),
      serviceType: this.eventData.serviceType,
      serviceCentre: workshopId,
      scheduledDate: this.scheduledDateValue,
      serviceItems: this.eventData.serviceItems ?? []
    });

    this.storeInitialValues();
  }

  private storeInitialValues(): void {
    if (!this.eventData) return;

    const currentValues = this.getCurrentEditableValues();

    this.initialFormValues = {
      serviceType: currentValues.serviceType,
      serviceCentre: currentValues.serviceCentre,
      scheduledDate: currentValues.scheduledDate
    };
    this.hasChanges = false;
  }

  private checkForChanges(): void {
    if (!this.initialFormValues) return;

    const currentValues = this.getCurrentEditableValues();
    this.hasChanges =
      currentValues.serviceType !== this.initialFormValues.serviceType ||
      currentValues.serviceCentre !== this.initialFormValues.serviceCentre ||
      currentValues.scheduledDate !== this.initialFormValues.scheduledDate;
  }

  private getCurrentEditableValues(): {
    serviceType: ServiceTypes | null;
    serviceCentre: number | null;
    scheduledDate: string | null;
  } {
    return {
      serviceType: this.form.get('serviceType')?.value ?? null,
      serviceCentre: this.form.get('serviceCentre')?.value ?? null,
      scheduledDate: this.normalizeScheduledDateValue(this.scheduledDateValue)
    };
  }

  private resolveWorkshopIdByName(name?: string | null): number | null {
    if (!name?.trim()) return null;
    const normalize = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase();
    const normalized = normalize(name);
    const match = this.serviceCentres.find(w => normalize(w.name) === normalized);

    return match?.id ?? null;
  }

  private formatScheduledForInput(date: Date): string {
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  private normalizeScheduledDateValue(value?: string | null): string | null {
    const trimmedValue = value?.trim() ?? '';
    return trimmedValue || null;
  }

  private formatOdometerReading(value: string): string {
    const trimmedValue = value?.trim() ?? '';

    if (!trimmedValue) {
      return '';
    }

    return /km$/i.test(trimmedValue) ? trimmedValue : `${trimmedValue} km`;
  }
}